import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiGetExamAttempts, apiGetWeakPoints, apiGetStudentLevels } from '../../utils/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Card, Badge, Button, ProgressBar, SectionHeader, Spinner } from '../../components/shared/UI';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-lg text-xs">
      <p className="font-semibold text-gray-700 mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-gray-600 capitalize">{p.name}:</span>
          <span className="font-medium">{p.value}%</span>
        </div>
      ))}
    </div>
  );
};

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ResultsPage() {
  const { user } = useAuth();
  const studentId = user?.studentId || user?.student?.id;

  const [attempts, setAttempts]       = useState([]);
  const [weakPoints, setWeakPoints]   = useState([]);
  const [levels, setLevels]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expanded, setExpanded]       = useState(null);

  useEffect(() => {
    if (!studentId) return;
    Promise.all([
      apiGetExamAttempts(studentId),
      apiGetWeakPoints(),
      apiGetStudentLevels(),
    ])
      .then(([attRes, wpRes, lvlRes]) => {
        setAttempts(attRes.attempts || []);
        setWeakPoints(wpRes.weakPoints || []);
        setLevels(lvlRes.levels || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  // Build chart data from attempts — group by month
  const chartData = (() => {
    if (!attempts.length) return [];
    const byMonth = {};
    attempts.filter(a => a.status === 'marked').forEach(a => {
      const m = new Date(a.completed_at || a.submitted_at).toLocaleString('en-US', { month: 'short' });
      if (!byMonth[m]) byMonth[m] = { month: m, reading: [], listening: [], writing: [] };
      byMonth[m].reading.push(a.reading_score);
      byMonth[m].listening.push(a.listening_score);
      byMonth[m].writing.push(a.writing_score);
    });
    return Object.values(byMonth).map(m => ({
      month: m.month,
      reading:   m.reading.length   ? Math.round(m.reading.reduce((s, v) => s + v, 0)   / m.reading.length)   : null,
      listening: m.listening.length ? Math.round(m.listening.reduce((s, v) => s + v, 0) / m.listening.length) : null,
      writing:   m.writing.length   ? Math.round(m.writing.reduce((s, v) => s + v, 0)   / m.writing.length)   : null,
    }));
  })();

  const scoreColor = (s) => s >= 80 ? 'text-green-600' : s >= 60 ? 'text-gold-600' : 'text-red-500';

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Spinner size="lg" className="text-brand-600" />
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

      {/* Progress chart */}
      <Card className="p-6">
        <SectionHeader title="Score Trend" subtitle="Your performance across Reading, Listening & Writing over time" />
        {chartData.length < 2 ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            Complete more exams to see your progress chart.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }} />
              <Line type="monotone" dataKey="reading"   stroke="#1830a2" strokeWidth={2.5} dot={{ r: 4 }} name="Reading" />
              <Line type="monotone" dataKey="listening" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4 }} name="Listening" />
              <Line type="monotone" dataKey="writing"   stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} name="Writing" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Course completion */}
      <Card className="p-6">
        <SectionHeader title="Course Completion" subtitle="Progress across all CEFR levels" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {levels.map(lv => (
            <div key={lv.level} className={`p-4 rounded-xl border-2 ${lv.isCompleted ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-bold text-gray-900 text-lg">{lv.level}</span>
                {lv.isCompleted ? <span className="text-xl">✅</span>
                  : lv.isUnlocked ? <span className="text-xl">🔄</span>
                  : <span className="text-xl">🔒</span>}
              </div>
              <div className="space-y-1.5 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>Exams taken</span>
                  <span className="font-medium">{lv.examAttempts}</span>
                </div>
                <div className="flex justify-between">
                  <span>Avg. score</span>
                  <span className={`font-medium ${lv.avgScore ? scoreColor(lv.avgScore) : 'text-gray-400'}`}>
                    {lv.avgScore ? `${lv.avgScore}%` : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Status</span>
                  <Badge variant={lv.isCompleted ? 'completed' : lv.isUnlocked ? 'active' : 'locked'} size="xs">
                    {lv.isCompleted ? '✅ Done' : lv.isUnlocked ? '⭐ Active' : '🔒 Locked'}
                  </Badge>
                </div>
              </div>
              {lv.avgScore && <div className="mt-3"><ProgressBar value={lv.avgScore} color={lv.isCompleted ? 'green' : 'gold'} showPercent={false} /></div>}
            </div>
          ))}
        </div>
      </Card>

      {/* Exam history */}
      <Card className="p-6">
        <SectionHeader title="Exam History" subtitle="Click any row to expand AI feedback" />
        {attempts.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No exam attempts yet. Take your first mock exam!</p>
        ) : (
          <div className="space-y-2">
            {attempts.map(attempt => {
              const fb = attempt.aiFeedback || {};
              return (
                <div key={attempt.id}>
                  <div
                    className={`rounded-xl border p-4 cursor-pointer transition-all hover:shadow-sm
                      ${expanded === attempt.id ? 'border-brand-300 bg-brand-50' : 'border-gray-100 hover:border-gray-200 bg-white'}`}
                    onClick={() => setExpanded(expanded === attempt.id ? null : attempt.id)}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-brand-50 flex items-center justify-center">
                          <span className="text-sm font-bold text-brand-500">{attempt.level}</span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{attempt.exam_title}</p>
                          <p className="text-xs text-gray-400">{fmt(attempt.completed_at || attempt.submitted_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {attempt.status === 'marked' ? (
                          <>
                            <div className="flex gap-3 text-xs text-gray-600">
                              <span>R: <span className={`font-semibold ${scoreColor(attempt.reading_score)}`}>{attempt.reading_score}%</span></span>
                              <span>L: <span className={`font-semibold ${scoreColor(attempt.listening_score)}`}>{attempt.listening_score}%</span></span>
                              <span>W: <span className={`font-semibold ${scoreColor(attempt.writing_score)}`}>{attempt.writing_score}%</span></span>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-sm font-bold ${scoreColor(attempt.total_score)} bg-white border`}>
                              {attempt.total_score}%
                            </div>
                          </>
                        ) : (
                          <Badge variant="pending">⏳ Marking…</Badge>
                        )}
                        <span className={`text-gray-400 transition-transform duration-200 ${expanded === attempt.id ? 'rotate-180' : ''}`}>▾</span>
                      </div>
                    </div>
                  </div>
                  {expanded === attempt.id && attempt.status === 'marked' && (
                    <div className="mx-2 mb-2 p-4 bg-purple-50 border border-purple-100 rounded-b-xl -mt-1 border-t-0 space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span>🤖</span>
                        <span className="text-xs font-semibold text-purple-800">AI Feedback</span>
                      </div>
                      {['reading', 'listening', 'writing'].map(sec => fb[sec] && (
                        <div key={sec} className="flex gap-3">
                          <span className="text-xs font-medium text-purple-600 capitalize w-16 flex-shrink-0 mt-0.5">{sec}</span>
                          <p className="text-xs text-gray-700 leading-relaxed">{fb[sec]}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* AI weak areas */}
      <Card className="p-6">
        <SectionHeader title="AI-Identified Weak Areas" subtitle="Recurring issues detected across all your sessions and exams" />
        {weakPoints.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No weak areas flagged yet. Keep practising!</p>
        ) : (
          <div className="space-y-3">
            {weakPoints.map((wp, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
                <div className={`mt-0.5 flex-shrink-0 w-3 h-3 rounded-full
                  ${wp.severity === 'high' ? 'bg-red-400' : wp.severity === 'medium' ? 'bg-yellow-400' : 'bg-blue-300'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-medium text-gray-800">{wp.description}</p>
                    <Badge variant={wp.severity === 'high' ? 'overdue' : wp.severity === 'medium' ? 'pending' : 'info'} size="xs">
                      {wp.severity}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 capitalize">{wp.type} · Flagged {wp.frequency} times</p>
                </div>
                <div className="w-24 flex-shrink-0">
                  <ProgressBar value={wp.frequency} max={10}
                    color={wp.severity === 'high' ? 'red' : wp.severity === 'medium' ? 'gold' : 'brand'}
                    showPercent={false} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
